import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { normCdf, NormalCurve } from './ZScoreCalc';

/* -------- Section 1: Probability of Two Events (Independent) -------- */
function TwoEventsSection() {
  const [pa, setPa] = useState(0.5);
  const [pb, setPb] = useState(0.4);
  const c = useMemo(() => {
    const notA = 1 - pa, notB = 1 - pb;
    const pAB = pa * pb;
    const aOrB = pa + pb - pAB;
    const symDiff = aOrB - pAB;
    const neither = 1 - aOrB;
    return { notA, notB, pAB, aOrB, symDiff, neither };
  }, [pa, pb]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900">Probability of Two Events</h3>
      <p className="text-sm text-slate-600 mt-1">To find out the union, intersection, and other related probabilities of two <b>independent</b> events.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg">
          <label className="text-sm">Probability of A: <b>P(A)</b><input type="number" step="0.01" min="0" max="1" value={pa} onChange={e => setPa(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
          <label className="text-sm">Probability of B: <b>P(B)</b><input type="number" step="0.01" min="0" max="1" value={pb} onChange={e => setPb(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
        </div>
        <p className="text-xs text-slate-500 mt-2">Please input values between 0 and 1.</p>
      </div>
      <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-slate-100"><tr><th className="px-3 py-2 text-left">Event</th><th className="px-3 py-2 text-right">Probability</th></tr></thead>
          <tbody>
            <tr><td className="px-3 py-2">P(A′) — A does NOT occur</td><td className="px-3 py-2 text-right font-mono">{c.notA.toFixed(4)}</td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">P(B′) — B does NOT occur</td><td className="px-3 py-2 text-right font-mono">{c.notB.toFixed(4)}</td></tr>
            <tr><td className="px-3 py-2">P(A ∩ B) — both occur</td><td className="px-3 py-2 text-right font-mono">{c.pAB.toFixed(4)}</td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">P(A ∪ B) — A or B or both</td><td className="px-3 py-2 text-right font-mono">{c.aOrB.toFixed(4)}</td></tr>
            <tr><td className="px-3 py-2">P(A △ B) — A or B but not both</td><td className="px-3 py-2 text-right font-mono">{c.symDiff.toFixed(4)}</td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">P((A ∪ B)′) — neither</td><td className="px-3 py-2 text-right font-mono">{c.neither.toFixed(4)}</td></tr>
          </tbody>
        </table>
        {/* Bar chart of the 6 probabilities */}
        <div className="mt-4" data-testid="prob-two-chart">
          <div className="text-xs font-semibold text-slate-600 mb-2">Visual comparison</div>
          <svg viewBox="0 0 480 200" className="w-full">
            {[
              ['P(A\')', c.notA, '#3b82f6'],
              ['P(B\')', c.notB, '#10b981'],
              ['P(A∩B)', c.pAB, '#f59e0b'],
              ['P(A∪B)', c.aOrB, '#8b5cf6'],
              ['P(A△B)', c.symDiff, '#ef4444'],
              ['P(neither)', c.neither, '#64748b'],
            ].map(([label, v, color], i) => (
              <g key={i}>
                <rect x={30 + i * 75} y={180 - v * 150} width="55" height={Math.max(0, v * 150)} fill={color} rx="3" />
                <text x={30 + i * 75 + 27} y={195} fontSize="9" fill="#334155" textAnchor="middle">{label}</text>
                <text x={30 + i * 75 + 27} y={180 - v * 150 - 4} fontSize="10" fill="#0f172a" textAnchor="middle" fontWeight="600">{v.toFixed(3)}</text>
              </g>
            ))}
            <line x1="20" y1="180" x2="470" y2="180" stroke="#94a3b8" />
          </svg>
        </div>
        {/* Venn diagram of A, B */}
        <div className="mt-4 flex flex-col md:flex-row gap-4" data-testid="prob-two-venn">
          <svg viewBox="0 0 320 220" className="w-full max-w-sm mx-auto">
            <rect x="4" y="4" width="312" height="212" fill="#f8fafc" stroke="#94a3b8" />
            <circle cx="120" cy="115" r="72" fill="#3b82f6" fillOpacity="0.35" stroke="#1d4ed8" strokeWidth="1.5"/>
            <circle cx="200" cy="115" r="72" fill="#ef4444" fillOpacity="0.35" stroke="#b91c1c" strokeWidth="1.5"/>
            <text x="85" y="60" fontSize="14" fontWeight="700" fill="#1d4ed8">A</text>
            <text x="230" y="60" fontSize="14" fontWeight="700" fill="#b91c1c">B</text>
            <text x="80" y="120" fontSize="11" fill="#0f172a">P(A only)</text>
            <text x="80" y="135" fontSize="12" fontWeight="700" fill="#0f172a">{(pa - c.pAB).toFixed(4)}</text>
            <text x="155" y="120" fontSize="11" fill="#0f172a" textAnchor="middle">P(A∩B)</text>
            <text x="160" y="135" fontSize="12" fontWeight="700" fill="#0f172a" textAnchor="middle">{c.pAB.toFixed(4)}</text>
            <text x="220" y="120" fontSize="11" fill="#0f172a">P(B only)</text>
            <text x="220" y="135" fontSize="12" fontWeight="700" fill="#0f172a">{(pb - c.pAB).toFixed(4)}</text>
            <text x="16" y="205" fontSize="11" fill="#64748b">P(neither) = {c.neither.toFixed(4)}</text>
          </svg>
          <div className="text-xs text-slate-600 space-y-1 flex-1">
            <div><span className="inline-block w-3 h-3 bg-blue-400 mr-1 align-middle rounded"></span>A region — P(A) = {pa.toFixed(4)}</div>
            <div><span className="inline-block w-3 h-3 bg-red-400 mr-1 align-middle rounded"></span>B region — P(B) = {pb.toFixed(4)}</div>
            <div><b>Intersection</b> — both events occur together</div>
            <div><b>Outside both circles</b> — neither event happens</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- Section 2: Probability Solver for Two Events -------- */
function SolverSection() {
  // 8 inputs matching Calculator.net; supply any 2, solve the rest.
  const [pa, setPa] = useState('0.5');
  const [pb, setPb] = useState('');
  const [notA, setNotA] = useState('');
  const [notB, setNotB] = useState('');
  const [pab, setPab] = useState('0.4');
  const [aOrB, setAOrB] = useState('');
  const [symDiff, setSymDiff] = useState('');
  const [neither, setNeither] = useState('');

  const parsed = useMemo(() => {
    const P = (v) => v === '' ? undefined : Number(v);
    return { pa: P(pa), pb: P(pb), notA: P(notA), notB: P(notB), pab: P(pab), aOrB: P(aOrB), symDiff: P(symDiff), neither: P(neither) };
  }, [pa, pb, notA, notB, pab, aOrB, symDiff, neither]);

  const { solved, steps, err } = useMemo(() => solveTwoEvents(parsed), [parsed]);

  const rowStyle = (isSolved) => isSolved ? 'text-[#4c7b25] font-semibold' : 'text-slate-900';
  const fld = (label, sym, val, setter, testid, isSolved) => (
    <div className="flex items-center gap-2 py-1.5">
      <label className={`flex-1 text-sm ${rowStyle(isSolved)}`}>Probability of {label}: <b>{sym}</b></label>
      <input type="number" step="any" value={isSolved && val === '' ? (solved[testid] != null ? solved[testid].toFixed(6) : '') : val} onChange={e => setter(e.target.value)}
        className={`w-32 px-3 py-1.5 rounded border ${isSolved ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-white'}`}
        data-testid={`solver-${testid}`} />
    </div>
  );

  const isSolvedKey = (k) => parsed[k] === undefined && solved && solved[k] != null;
  const clear = () => { setPa(''); setPb(''); setNotA(''); setNotB(''); setPab(''); setAOrB(''); setSymDiff(''); setNeither(''); };

  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Probability Solver for Two Events</h3>
      <p className="text-sm text-slate-600 mt-1">Please provide any 2 values below to calculate the rest probabilities of two independent events.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="max-w-2xl">
          {fld('A', 'P(A)', pa, setPa, 'pa', isSolvedKey('pa'))}
          {fld('B', 'P(B)', pb, setPb, 'pb', isSolvedKey('pb'))}
          {fld("A NOT occurring", "P(A')", notA, setNotA, 'notA', isSolvedKey('notA'))}
          {fld("B NOT occurring", "P(B')", notB, setNotB, 'notB', isSolvedKey('notB'))}
          {fld('A and B both occurring', 'P(A∩B)', pab, setPab, 'pab', isSolvedKey('pab'))}
          {fld('A or B or both', 'P(A∪B)', aOrB, setAOrB, 'aOrB', isSolvedKey('aOrB'))}
          {fld('A or B but NOT both', 'P(A△B)', symDiff, setSymDiff, 'symDiff', isSolvedKey('symDiff'))}
          {fld('neither A nor B', "P((A∪B)')", neither, setNeither, 'neither', isSolvedKey('neither'))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={clear} data-testid="solver-clear" className="px-4 py-1.5 rounded bg-slate-300 text-slate-800 text-sm font-medium">Clear</button>
          <span className="text-xs text-slate-500">Please input values between 0 and 1.</span>
        </div>
      </div>
      {err && <div className="mt-3 text-red-600 text-sm bg-red-50 border border-red-200 rounded p-3" data-testid="solver-err">{err}</div>}
      {!err && steps.length > 0 && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="solver-steps">
          <h4 className="text-sm font-bold text-slate-900 mb-2">Steps</h4>
          <div className="space-y-2 text-sm font-mono">
            {steps.map((s, i) => (
              <div key={i} dangerouslySetInnerHTML={{ __html: s }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Given up to 8 inputs, derive the remaining. Uses PA, PB, PAB as canonical. */
function solveTwoEvents(inp) {
  const given = Object.entries(inp).filter(([, v]) => v !== undefined && !isNaN(v));
  if (given.length < 2) return { solved: null, steps: [], err: `Please provide any 2 values (${given.length} given).` };

  const st = [];
  let { pa, pb, notA, notB, pab, aOrB, symDiff, neither } = inp;
  // Complementary derivations first
  const push = (line) => st.push(line);
  const set = (name, val, line) => {
    if (({ pa, pb, notA, notB, pab, aOrB, symDiff, neither })[name] === undefined && val != null && isFinite(val)) {
      if (name === 'pa') pa = val;
      else if (name === 'pb') pb = val;
      else if (name === 'notA') notA = val;
      else if (name === 'notB') notB = val;
      else if (name === 'pab') pab = val;
      else if (name === 'aOrB') aOrB = val;
      else if (name === 'symDiff') symDiff = val;
      else if (name === 'neither') neither = val;
      if (line) push(line);
    }
  };

  for (let iter = 0; iter < 10; iter++) {
    // Complements
    if (pa != null && notA == null) set('notA', 1 - pa, `<b>P(A')</b> = 1 − P(A) = 1 − ${pa} = <b>${(1 - pa).toFixed(6)}</b>`);
    if (notA != null && pa == null) set('pa', 1 - notA, `<b>P(A)</b> = 1 − P(A') = 1 − ${notA} = <b>${(1 - notA).toFixed(6)}</b>`);
    if (pb != null && notB == null) set('notB', 1 - pb, `<b>P(B')</b> = 1 − P(B) = 1 − ${pb} = <b>${(1 - pb).toFixed(6)}</b>`);
    if (notB != null && pb == null) set('pb', 1 - notB, `<b>P(B)</b> = 1 − P(B') = 1 − ${notB} = <b>${(1 - notB).toFixed(6)}</b>`);
    if (aOrB != null && neither == null) set('neither', 1 - aOrB, `<b>P((A∪B)')</b> = 1 − P(A∪B) = 1 − ${aOrB} = <b>${(1 - aOrB).toFixed(6)}</b>`);
    if (neither != null && aOrB == null) set('aOrB', 1 - neither, `<b>P(A∪B)</b> = 1 − P((A∪B)') = 1 − ${neither} = <b>${(1 - neither).toFixed(6)}</b>`);

    // Given P(A) and P(A∩B): P(B) = P(A∩B) / P(A)   (independent)
    if (pa != null && pab != null && pb == null && pa !== 0) set('pb', pab / pa, `<b>P(B)</b> = P(A∩B) / P(A) = ${pab} / ${pa} = <b>${(pab / pa).toFixed(6)}</b>`);
    if (pb != null && pab != null && pa == null && pb !== 0) set('pa', pab / pb, `<b>P(A)</b> = P(A∩B) / P(B) = ${pab} / ${pb} = <b>${(pab / pb).toFixed(6)}</b>`);
    // Given P(A) and P(B): assume independence for P(A∩B) if not otherwise known
    if (pa != null && pb != null && pab == null) set('pab', pa * pb, `<b>P(A∩B)</b> = P(A) × P(B) = ${pa} × ${pb} = <b>${(pa * pb).toFixed(6)}</b> &nbsp;<i class="text-slate-500">(assuming independence)</i>`);
    // Given P(A∪B) with P(A) or P(B)
    if (aOrB != null && pa != null && pb != null && pab == null) set('pab', pa + pb - aOrB, `<b>P(A∩B)</b> = P(A) + P(B) − P(A∪B) = ${pa} + ${pb} − ${aOrB} = <b>${(pa + pb - aOrB).toFixed(6)}</b>`);
    if (aOrB != null && pa != null && pab != null && pb == null) set('pb', aOrB - pa + pab, `<b>P(B)</b> = P(A∪B) − P(A) + P(A∩B) = ${aOrB} − ${pa} + ${pab} = <b>${(aOrB - pa + pab).toFixed(6)}</b>`);
    if (aOrB != null && pb != null && pab != null && pa == null) set('pa', aOrB - pb + pab, `<b>P(A)</b> = P(A∪B) − P(B) + P(A∩B) = ${aOrB} − ${pb} + ${pab} = <b>${(aOrB - pb + pab).toFixed(6)}</b>`);
    // P(A∪B) from P(A), P(B), P(A∩B)
    if (pa != null && pb != null && pab != null && aOrB == null) set('aOrB', pa + pb - pab, `<b>P(A∪B)</b> = P(A) + P(B) − P(A∩B) = ${pa} + ${pb} − ${pab} = <b>${(pa + pb - pab).toFixed(6)}</b>`);
    // Symmetric difference
    if (pa != null && pb != null && pab != null && symDiff == null) set('symDiff', pa + pb - 2 * pab, `<b>P(A△B)</b> = P(A) + P(B) − 2·P(A∩B) = ${pa} + ${pb} − 2×${pab} = <b>${(pa + pb - 2 * pab).toFixed(6)}</b>`);
    if (symDiff != null && aOrB != null && pab == null) set('pab', (aOrB - symDiff), `<b>P(A∩B)</b> = P(A∪B) − P(A△B) = ${aOrB} − ${symDiff} = <b>${(aOrB - symDiff).toFixed(6)}</b>`);
  }
  const solved = { pa, pb, notA, notB, pab, aOrB, symDiff, neither };
  const unresolved = Object.entries(solved).filter(([, v]) => v == null || isNaN(v));
  const err = unresolved.length ? `Cannot uniquely solve — need more information (missing: ${unresolved.map(([k]) => k).join(', ')}).` : '';
  return { solved, steps: st, err };
}

/* -------- Section 3: Series of Independent Events (custom dependent/independent) -------- */
function SeriesSection() {
  const [events, setEvents] = useState([
    { p: 0.6, times: 5 },
    { p: 0.3, times: 3 }
  ]);
  const setEv = (i, key, v) => {
    const arr = [...events]; arr[i] = { ...arr[i], [key]: v }; setEvents(arr);
  };
  const addEv = () => setEvents([...events, { p: 0.5, times: 1 }]);
  const remEv = i => setEvents(events.filter((_, k) => k !== i));

  const c = useMemo(() => {
    let pAll = 1;
    let noneProduct = 1;
    events.forEach(e => {
      const p = Math.max(0, Math.min(1, +e.p || 0));
      const n = Math.max(0, Math.floor(+e.times || 0));
      pAll *= Math.pow(p, n);
      noneProduct *= Math.pow(1 - p, n);
    });
    return { pAll, atLeastOne: 1 - noneProduct, none: noneProduct };
  }, [events]);

  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Probability of a Series of Independent Events</h3>
      <p className="text-sm text-slate-600 mt-1">Calculate combined probability where each event occurs a given number of times.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="mb-2 text-xs text-slate-600">Events:</div>
        {events.map((e, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-sm w-6 text-slate-700">{String.fromCharCode(65 + i)}:</span>
            <span className="text-xs text-slate-600">P =</span>
            <input type="number" step="0.01" min="0" max="1" value={e.p} onChange={ev => setEv(i, 'p', ev.target.value)} className="px-2 py-1.5 rounded border border-slate-300 bg-white w-24" />
            <span className="text-xs text-slate-600">Repeat Times =</span>
            <input type="number" min="0" value={e.times} onChange={ev => setEv(i, 'times', ev.target.value)} className="px-2 py-1.5 rounded border border-slate-300 bg-white w-24" />
            {events.length > 1 && <button onClick={() => remEv(i)} className="text-red-500 text-xs">Remove</button>}
          </div>
        ))}
        <button onClick={addEv} className="text-blue-600 text-sm font-medium">+ Add event</button>
      </div>
      <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
        <table className="w-full text-sm border border-slate-200" data-testid="prob-series-result">
          <tbody>
            <tr><td className="px-3 py-2">P(all events occur their specified times) = ∏ pᵢ^nᵢ</td><td className="px-3 py-2 text-right font-mono">{c.pAll.toExponential(6)}</td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">P(at least one event occurs) = 1 − ∏ (1−pᵢ)^nᵢ</td><td className="px-3 py-2 text-right font-mono">{c.atLeastOne.toFixed(6)}</td></tr>
            <tr><td className="px-3 py-2">P(none of the events occur) = ∏ (1−pᵢ)^nᵢ</td><td className="px-3 py-2 text-right font-mono">{c.none.toFixed(6)}</td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">Expected number of successes = Σ nᵢ · pᵢ</td><td className="px-3 py-2 text-right font-mono">{events.reduce((a, e) => a + (Math.max(0, Math.floor(+e.times || 0)) * Math.max(0, Math.min(1, +e.p || 0))), 0).toFixed(4)}</td></tr>
          </tbody>
        </table>
      </div>
      {events.length <= 4 && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm font-mono" data-testid="prob-series-steps">
          <h4 className="text-slate-900 font-bold mb-2 not-italic font-sans">Steps</h4>
          <div className="space-y-1">
            {events.map((e, i) => {
              const p = Math.max(0, Math.min(1, +e.p || 0));
              const n = Math.max(0, Math.floor(+e.times || 0));
              const L = String.fromCharCode(65 + i);
              const pn = Math.pow(p, n);
              const notP = Math.pow(1 - p, n);
              return (
                <div key={i} className="space-y-0.5">
                  <div>Probability of {L} occurring {n} time(s) = {p}<sup>{n}</sup> = {pn.toPrecision(6)}</div>
                  <div>Probability of {L} NOT occurring = (1 − {p})<sup>{n}</sup> = {notP.toPrecision(6)}</div>
                  <div>Probability of {L} occurring = 1 − (1 − {p})<sup>{n}</sup> = {(1 - notP).toPrecision(6)}</div>
                </div>
              );
            })}
            <div className="pt-2 border-t border-blue-200">
              Probability of all events occurring together = {events.map(e => `${+e.p || 0}^${Math.floor(+e.times || 0)}`).join(' × ')} = <b>{c.pAll.toPrecision(6)}</b>
            </div>
            <div>Probability that none of the events occur = {events.map(e => `(1 − ${+e.p || 0})^${Math.floor(+e.times || 0)}`).join(' × ')} = <b>{c.none.toPrecision(6)}</b></div>
            <div>Probability of at least one event = 1 − P(none) = <b>{c.atLeastOne.toPrecision(6)}</b></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------- Section 4: Probability of a Normal Distribution -------- */
function NormalSection() {
  const [mean, setMean] = useState(0);
  const [sd, setSd] = useState(1);
  const [lb, setLb] = useState(-1);
  const [rb, setRb] = useState(1);
  const [lbInf, setLbInf] = useState(false);
  const [rbInf, setRbInf] = useState(false);
  const c = useMemo(() => {
    if (sd <= 0) return null;
    const zLo = lbInf ? -Infinity : (lb - mean) / sd;
    const zHi = rbInf ? Infinity : (rb - mean) / sd;
    const p = normCdf(zHi === Infinity ? 40 : zHi) - normCdf(zLo === -Infinity ? -40 : zLo);
    return { zLo, zHi, p };
  }, [mean, sd, lb, rb, lbInf, rbInf]);
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Probability of a Normal Distribution</h3>
      <p className="text-sm text-slate-600 mt-1">Find the area P under a normal curve given the mean, standard deviation, and range bounds.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">Mean (μ)<input type="number" step="any" value={mean} onChange={e => setMean(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
        <label className="text-sm">Standard Deviation (σ)<input type="number" step="any" value={sd} onChange={e => setSd(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
        <label className="text-sm">Left Bound (Lb)
          <div className="flex items-center gap-2 mt-1">
            <input type="number" step="any" disabled={lbInf} value={lb} onChange={e => setLb(+e.target.value || 0)} className="flex-1 px-3 py-2 rounded border border-slate-300 bg-white disabled:bg-slate-100" />
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={lbInf} onChange={e => setLbInf(e.target.checked)} /> −∞</label>
          </div>
        </label>
        <label className="text-sm">Right Bound (Rb)
          <div className="flex items-center gap-2 mt-1">
            <input type="number" step="any" disabled={rbInf} value={rb} onChange={e => setRb(+e.target.value || 0)} className="flex-1 px-3 py-2 rounded border border-slate-300 bg-white disabled:bg-slate-100" />
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={rbInf} onChange={e => setRbInf(e.target.checked)} /> +∞</label>
          </div>
        </label>
      </div>
      {c && (
        <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4" data-testid="prob-normal-result">
          <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
            <div>
              <div className="text-lg text-slate-800">
                The probability between {lbInf ? '−∞' : lb} and {rbInf ? '+∞' : rb} is <span className="text-emerald-700 font-bold text-xl" data-testid="norm-p-between">{c.p.toFixed(5)}</span>
              </div>
              <div className="text-sm text-slate-700 mt-2">
                The probability outside of {lbInf ? '−∞' : lb} and {rbInf ? '+∞' : rb} is 1 − {c.p.toFixed(5)} = <b>{(1 - c.p).toFixed(5)}</b>
              </div>
              <div className="text-sm text-slate-700">
                The probability of {lbInf ? '−∞' : lb} or less (≤ {lbInf ? '−∞' : lb}) is <b>{normCdf(c.zLo === -Infinity ? -40 : c.zLo).toFixed(5)}</b>
              </div>
              <div className="text-sm text-slate-700">
                The probability of {rbInf ? '+∞' : rb} or more (≥ {rbInf ? '+∞' : rb}) is <b>{(1 - normCdf(c.zHi === Infinity ? 40 : c.zHi)).toFixed(5)}</b>
              </div>
            </div>
            <NormalCurve type={lbInf && !rbInf ? 'lt' : !lbInf && rbInf ? 'gt' : 'between'} z1={c.zLo === -Infinity ? -4 : c.zLo} z2={c.zHi === Infinity ? 4 : c.zHi} />
          </div>
          {/* Confidence Intervals Table */}
          <div className="mt-4">
            <h4 className="font-bold text-slate-900 mb-2">Confidence Intervals Table:</h4>
            <table className="w-full text-sm border border-slate-300" data-testid="norm-conf-table">
              <thead className="bg-[#325487] text-white">
                <tr><th className="px-3 py-2 text-left">Confidence</th><th className="px-3 py-2 text-left">Range</th><th className="px-3 py-2 text-left">n</th></tr>
              </thead>
              <tbody>
                {[
                  [0.6828, 1.00000],
                  [0.80, 1.28155],
                  [0.90, 1.64485],
                  [0.95, 1.95996],
                  [0.98, 2.32635],
                  [0.99, 2.57583],
                  [0.995, 2.80703],
                  [0.998, 3.09023],
                  [0.999, 3.29053],
                  [0.9999, 3.89059],
                  [0.99999, 4.41717],
                ].map(([conf, n], i) => (
                  <tr key={i} className={i % 2 === 1 ? 'bg-slate-50' : ''}>
                    <td className="px-3 py-1.5">{conf}</td>
                    <td className="px-3 py-1.5 font-mono">−{n.toFixed(5)}–{n.toFixed(5)}</td>
                    <td className="px-3 py-1.5 font-mono">{n.toFixed(12)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProbabilityCalc() {
  return (
    <CalcShell look="cnet" title="Probability Calculator" subtitle="Modify the values and click the Calculate button to use">
      <TwoEventsSection />
      <SolverSection />
      <SeriesSection />
      <NormalSection />
    </CalcShell>
  );
}
